import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { setHandleImpressionData, setImpressionData } from '../../store/UploadFile/UploadFileSlice';
import { getFormToken } from '../../util/file';
import { useLang } from '../../util/lang';
import NewPatientPhotos from '../NewPatient/Photos/NewPatientPhotos';
import ImpressionTabs from '../NewPatient/Photos/ImpressionTabs';
import { validateImpressionFiles } from '../NewPatient/NewPatientUtill';
import useThreeShapeOnRole from '../../hooks/useThreeShapeOnRole';

const RESTORATIVE_SECTIONS = [
    {
        id: 'toothShape',
        titleKey: '@PatientInfo.smileDesign.restorativePhase.toothShapeAdjustments',
        fileSlots: { upper: 'tooth_shape_upper', lower: 'tooth_shape_lower', bite: 'tooth_shape_bite' },
        fileFields: [
            { id: 'tooth_shape_upper', key: 'stl_file0', token: '$$FILE:stl0' },
            { id: 'tooth_shape_lower', key: 'stl_file1', token: '$$FILE:stl1' },
            { id: 'tooth_shape_bite', key: 'stl_file2', token: '$$FILE:stl2' },
        ],
    },
    {
        id: 'mockup',
        titleKey: '@PatientInfo.smileDesign.restorativePhase.mockupAdjustments',
        fileSlots: { upper: 'mockup_upper', lower: 'mockup_lower', bite: 'mockup_bite' },
        fileFields: [
            { id: 'mockup_upper', key: 'mockupUpperStl', token: '$$FILE:mockupUpperStl' },
            { id: 'mockup_lower', key: 'mockupLowerStl', token: '$$FILE:mockupLowerStl' },
            { id: 'mockup_bite', key: 'mockupBiteStl', token: '$$FILE:mockupBiteStl' },
        ],
    },
];

/** Creates the independent upload state for a restorative impression section. */
const createRestorativeSectionData = (section) => ({
    impressionType: 'FILE',
    scannerBrand: '',
    doctorId: '',
    modernCode: '',
    waybillNumber: '',
    courierCompany: '',
    files: section.fileFields.map(({ id }) => ({
        id,
        category: 'stl_file',
        data: null,
        dataURL: null,
        uploaded: false,
        upload_later: false,
        will_not_upload: false,
    })),
});

/** Copies non-file impression details into the case impression payload. */
const applyRestorativeImpressionDetails = (caseImpression, sectionData) => {
    caseImpression.type = sectionData.impressionType;
    caseImpression.modern_code = sectionData.modernCode;
    caseImpression.waybill_number = sectionData.waybillNumber;
    caseImpression.courier_company = sectionData.courierCompany;
    caseImpression.scanner_brand = sectionData.scannerBrand;
    caseImpression.doctor_id = sectionData.doctorId;
};

/** Validates the selected restorative impression method for one section. */
const isRestorativeImpressionValid = (sectionData, isRequired) => {
    if (!isRequired) return true;
    if (sectionData.impressionType === 'COURIER') return !String.isBlank(sectionData.modernCode);
    if (sectionData.impressionType === 'SCANNER') {
        return !String.isBlank(sectionData.scannerBrand) && !String.isBlank(sectionData.doctorId);
    }
    return true;
};

const hasRestorativeImpressionDetails = (caseImpression) =>
    Boolean(
        caseImpression.stl_file0 ||
            caseImpression.stl_file1 ||
            caseImpression.stl_file2 ||
            caseImpression.modern_code ||
            caseImpression.waybill_number ||
            caseImpression.courier_company ||
            caseImpression.scanner_brand ||
            caseImpression.doctor_id
    );

const hasMockupFeedbackFiles = (mockupFeedbackFiles) =>
    Object.values(mockupFeedbackFiles).some((value) => Boolean(value));

/** Renders the restorative workflow's applicable impression upload sections. */
const RestorativeUploadImpressionTab = ({ hasMockupOrder = true, isToothShapeRequired = false }) => {
    const dispatch = useDispatch();
    const get = useLang();
    const { threeshapeConnected, setThreeshapeConnected } = useThreeShapeOnRole();
    const { control, getValues, setValue } = useFormContext();
    const initialSectionData = useMemo(
        () =>
            RESTORATIVE_SECTIONS.reduce((result, section) => {
                result[section.id] = createRestorativeSectionData(section);
                return result;
            }, {}),
        []
    );
    const watchedSectionData = useWatch({ control, name: 'restorativeImpression' });
    const sectionData = watchedSectionData || initialSectionData;
    const sections = useMemo(
        () => (hasMockupOrder ? RESTORATIVE_SECTIONS : RESTORATIVE_SECTIONS.filter((section) => section.id !== 'mockup')),
        [hasMockupOrder]
    );

    /** Initializes the restorative impression fields in the shared form state. */
    useEffect(() => {
        if (!getValues('restorativeImpression')) {
            setValue('restorativeImpression', initialSectionData);
        }
    }, [getValues, initialSectionData, setValue]);

    /** Replaces the data held by one restorative impression section. */
    const updateSectionData = (sectionId, data) => {
        setValue('restorativeImpression', { ...sectionData, [sectionId]: data });
    };

    /** Updates one file without sharing file state between the two sections. */
    const updateSectionFile = (sectionId, fileId, value, platform) => {
        const section = sectionData[sectionId];
        setValue('restorativeImpression', {
            ...sectionData,
            [sectionId]: {
                ...section,
                files: section.files.map((file) => {
                    if (file.id !== fileId) return file;
                    if (value === 'remove') {
                        return { ...file, data: null, dataURL: null, uploaded: false, platform: undefined };
                    }
                    return { ...file, data: value, dataURL: value, uploaded: true, platform };
                }),
            },
        });
    };

    /** Packages the applicable restorative STL files using their required multipart tokens. */
    const handleDataSave = useCallback(async () => {
        if (isToothShapeRequired === null) return;
        const form = new FormData();
        const case_impression = { stl_files_later: false };
        const mockup_feedback_files = {};
        let isValid = true;

        sections.forEach((section) => {
            const currentSectionData = sectionData[section.id];
            const files = currentSectionData.files;
            const isRequired = section.id === 'toothShape' && isToothShapeRequired;
            if (section.id === 'toothShape') {
                applyRestorativeImpressionDetails(case_impression, currentSectionData);
                if (!isRestorativeImpressionValid(currentSectionData, isRequired)) isValid = false;
            }
            if (currentSectionData.impressionType !== 'FILE') return;
            section.fileFields.forEach(({ id, key, token }) => {
                const file = files.find((item) => item.id === id);
                if (isRequired && !file?.data) isValid = false;
                const fileToken = getFormToken(form, file?.data, token);
                if (!fileToken) return;
                if (section.id === 'toothShape') case_impression[key] = fileToken;
                else mockup_feedback_files[key] = fileToken;
            });
        });

        if (!isValid) return;
        const impressionData = {
            ...(hasRestorativeImpressionDetails(case_impression) && { case_impression }),
            ...(hasMockupOrder && hasMockupFeedbackFiles(mockup_feedback_files) && { mockup_feedback_files }),
        };
        dispatch(setImpressionData(impressionData));
        return { impressionFile: form, ...impressionData };
    }, [dispatch, hasMockupOrder, isToothShapeRequired, sectionData, sections]);

    useEffect(() => {
        dispatch(setHandleImpressionData(handleDataSave));
    }, [dispatch, handleDataSave]);

    return (
        <div>
            {sections.map((section, index) => {
                const isToothShapeSection = section.id === 'toothShape';
                const isRequired = isToothShapeSection && isToothShapeRequired;

                return (
                    <div key={section.id}>
                        <div
                            className="restorative-section-heading"
                            style={{ fontSize: 20 }}
                        >
                            {get(section.titleKey)}
                            {isRequired && <span style={{ color: '#f44336' }}> *</span>}
                        </div>
                        {<h3>{get('@NewPatient.impressions.uploadImpression')}</h3>}
                        <ImpressionTabs
                            mode="edit"
                            uploadLater="patientInfo"
                            checkInvalid={() => {}}
                            impressionData={sectionData[section.id]}
                            onImpressionDataChange={(data) => updateSectionData(section.id, data)}
                            fileSlots={section.fileSlots}
                            hideBiteTwo
                            handleChange={(fileId, file, platform) => updateSectionFile(section.id, fileId, file, platform)}
                            threeshapeConnected={threeshapeConnected}
                            handleThreeshapeConnected={setThreeshapeConnected}
                        />
                    </div>
                );
            })}
        </div>
    );
};

/** Renders the existing shared impression uploader for non-restorative pages. */
const DefaultUploadImpressionTab = ({ caseId }) => {
    const newPatientData = useSelector((state) => state.newPatientReducer);
    const get = useLang();
    const dispatch = useDispatch();
    const { threeshapeConnected, setThreeshapeConnected } = useThreeShapeOnRole();

    /** Stores the legacy impression validation state in Redux. */
    const handleNewPatientDataError = (errorMsg) => {
        dispatch({ type: 'NEW_PATIENT_DATA_ERROR', errorMsg: errorMsg });
    };
    const errorRef = useRef(null);

    /** Builds the legacy case impression payload without altering its field mapping. */
    const getCaseImpressionCaseFileData = useCallback(() => {
        const form = new FormData();
        let modifiedKey,
            case_impression = {},
            stlCounter = 0,
            newImpressionUpload = {};
        const newState = { ...newPatientData };
        const files = newState.files;

        files.forEach((value) => {
            if (value.category == 'stl_file') {
                const fileName = `$$FILE:stl_file${stlCounter}`;
                case_impression['stl_file' + stlCounter] = getFormToken(form, value.data, fileName);
                case_impression.stl_files_later = value.will_not_upload ? null : value.upload_later;
                stlCounter++;
            }
        });
        Object.keys(newState).forEach(function (key) {
            modifiedKey = key
                .replace(/(?:^|\.?)([A-Z])/g, function (x, y) {
                    return '_' + y.toLowerCase();
                })
                .replace(/^_/, '');

            if (
                modifiedKey == 'scanner_brand' ||
                modifiedKey == 'doctor_id' ||
                modifiedKey == 'modern_code' ||
                modifiedKey === 'waybill_number' ||
                modifiedKey === 'courier_company'
            ) {
                if (newState[key] && newState[key].length > 0) case_impression[modifiedKey] = newState[key];
            }
        });

        newImpressionUpload.case_id = caseId;
        newImpressionUpload.doctor_id = newPatientData.doctorId;
        case_impression.type = newState.impressionType;

        switch (newPatientData.impressionType) {
            case 'FILE': {
                const impressionErrList = validateImpressionFiles(
                    newPatientData.files.filter((value) => value.category === 'stl_file')
                );
                handleNewPatientDataError({
                    id: 'impression',
                    msg: impressionErrList.length > 0 ? get('@NewPatient.warning.allStl') : '',
                    valid: impressionErrList.length === 0,
                });
                break;
            }
            case 'COURIER': {
                const valid = !String.isBlank(newPatientData.modernCode);
                handleNewPatientDataError({
                    id: 'impression',
                    msg: valid ? '' : get('@NewPatient.warning.courier'),
                    valid,
                });
                break;
            }
            case 'SCANNER': {
                const valid = !String.isBlank(newPatientData.scannerBrand) && !String.isBlank(newPatientData.doctorId);
                handleNewPatientDataError({
                    id: 'impression',
                    msg: valid ? '' : get('@NewPatient.warning.scanner'),
                    valid,
                });
                break;
            }
            default:
                break;
        }

        return [form, newImpressionUpload, case_impression];
    }, [newPatientData]);

    /** Validates and returns the legacy impression submission payload. */
    const handleDataSave = async () => {
        try {
            const [form, newImpressionUpload, case_impression] = getCaseImpressionCaseFileData();
            if (!Array.isArray(newPatientData.errors)) return;
            const impressionError = newPatientData.errors.find((value) => value.id === 'impression');
            if (!impressionError) return;
            if (impressionError.valid) {
                dispatch(setImpressionData({ case_impression, newImpressionUpload }));
                return { impressionFile: form, case_impression, newImpressionUpload };
            }
            if (errorRef.current) {
                errorRef.current.scrollIntoView({ behavior: 'smooth' });
            }
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        dispatch(setHandleImpressionData(handleDataSave));
    }, [newPatientData]);

    /** Renders the legacy impression validation message. */
    const renderImpressionError = () => {
        if (!Array.isArray(newPatientData.errors)) return null;
        const impressionError = newPatientData.errors.find((value) => value.id === 'impression');
        if (impressionError && !impressionError.valid) {
            return <div style={{ marginLeft: 12, color: 'red' }}>{impressionError.msg}</div>;
        }
        return null;
    };

    return (
        <div>
            <NewPatientPhotos
                tabMode="impression"
                uploadLater="patientInfo"
                threeshapeConnected={threeshapeConnected}
                handleThreeshapeConnected={setThreeshapeConnected}
            />
            <div ref={errorRef} style={{ height: '100%', minHeight: 100 }}>
                {renderImpressionError()}
            </div>
        </div>
    );
};

/** Selects the independent restorative uploader only when explicitly requested. */
const UploadImpressionTab = ({ caseId, mode, hasMockupOrder, isToothShapeRequired }) =>
    mode === 'restorative' ? (
        <RestorativeUploadImpressionTab
            hasMockupOrder={hasMockupOrder}
            isToothShapeRequired={isToothShapeRequired}
        />
    ) : (
        <DefaultUploadImpressionTab caseId={caseId} />
    );

export default UploadImpressionTab;
